import React, { useState, useEffect } from 'react';
import { Users, UserCog, TrendingUp, DollarSign, Target, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ManagerDashboard = ({ manager }) => {
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    totalClients: 0,
    totalRevenue: 0,
    avgPerformance: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [agentsRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/sales-agents`),
        axios.get(`${BACKEND_URL}/api/users`)
      ]);

      const agents = agentsRes.data;
      const users = usersRes.data.filter(u => u.role === 'user');
      
      const totalRevenue = users.reduce((sum, u) => sum + (u.lifetimeValue || 0), 0);
      const activeAgents = agents.filter(a => a.isActive).length;

      setStats({
        totalAgents: agents.length,
        activeAgents,
        totalClients: users.length,
        totalRevenue,
        avgPerformance: agents.length > 0 ? (totalRevenue / agents.length) : 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Welcome, {manager.name}! 👋</h2>
        <p className="text-purple-100">Manage your entire sales team from one place</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <UserCog className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Total Agents</p>
            <p className="text-3xl font-bold text-slate-800">{stats.totalAgents}</p>
            <p className="text-sm text-green-600 mt-2">{stats.activeAgents} active</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Total Clients</p>
            <p className="text-3xl font-bold text-slate-800">{stats.totalClients}</p>
            <p className="text-sm text-slate-500 mt-2">Across all agents</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Total Revenue</p>
            <p className="text-3xl font-bold text-slate-800">₹{stats.totalRevenue.toLocaleString()}</p>
            <p className="text-sm text-slate-500 mt-2">All-time</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Avg Performance</p>
            <p className="text-3xl font-bold text-slate-800">₹{Math.round(stats.avgPerformance).toLocaleString()}</p>
            <p className="text-sm text-slate-500 mt-2">Per agent</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Conversion Rate</p>
            <p className="text-3xl font-bold text-slate-800">0%</p>
            <p className="text-sm text-slate-500 mt-2">Coming soon</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
              <Award className="w-6 h-6 text-cyan-600" />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1">Top Performer</p>
            <p className="text-lg font-bold text-slate-800">-</p>
            <p className="text-sm text-slate-500 mt-2">No data yet</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left">
            <UserCog className="w-6 h-6 text-purple-600 mb-2" />
            <p className="font-semibold text-slate-800">Add New Agent</p>
            <p className="text-sm text-slate-500">Create sales agent account</p>
          </button>
          <button className="p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left">
            <Users className="w-6 h-6 text-blue-600 mb-2" />
            <p className="font-semibold text-slate-800">Assign Clients</p>
            <p className="text-sm text-slate-500">Distribute clients to agents</p>
          </button>
          <button className="p-4 border-2 border-dashed border-slate-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left">
            <TrendingUp className="w-6 h-6 text-green-600 mb-2" />
            <p className="font-semibold text-slate-800">View Reports</p>
            <p className="text-sm text-slate-500">Detailed performance analytics</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;